import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { LOGIN } from "@/constants/testIds";
import { ArrowRight, GoogleLogo, Eye, EyeSlash } from "@phosphor-icons/react";

const AUTH_IMG =
  "https://images.unsplash.com/photo-1566097127420-26750d93591e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHx2aW50YWdlJTIwZnVybml0dXJlJTIwZWRpdG9yaWFsfGVufDB8fHx8MTc4Mjg3MDgzMHww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login, googleLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not sign you in");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FDFBF7]" data-testid="login-page">
      <div className="flex flex-col justify-center px-6 sm:px-14 py-12 max-w-xl w-full mx-auto vv-fade-up">
        <Link to="/" className="flex items-center gap-3 mb-10">
          <img src="/vv_logo.jpg" alt="Vintage Vantage seal" className="h-12 w-12 rounded-full object-cover ring-1 ring-[#E5E0D6]" />
          <span className="font-serif-display text-2xl font-bold tracking-tight">Vintage Vantage</span>
        </Link>

        <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-none font-medium mb-2">Welcome back.</h1>
        <p className="text-[17px] text-[#525252] leading-relaxed mb-8">Sign in to your owned portfolio and pick up where you left off.</p>

        <button
          data-testid="login-google-button"
          onClick={googleLogin}
          className="w-full h-14 rounded-none border border-[#0D1B2A] font-medium text-[17px] inline-flex items-center justify-center gap-3 hover:bg-[#F5F3ED] transition-colors"
        >
          <GoogleLogo size={22} weight="bold" /> Continue with Google
        </button>

        <div className="flex items-center gap-4 my-6">
          <span className="h-px flex-1 bg-[#D8D3C8]" />
          <span className="label-caps">or with email</span>
          <span className="h-px flex-1 bg-[#D8D3C8]" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label-caps block mb-1.5">Email</label>
            <input
              data-testid={LOGIN.emailInput}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="h-14 border border-[#0D1B2A]/30 bg-transparent rounded-none px-4 text-[17px] focus:border-[#0D1B2A] focus:ring-1 focus:ring-[#0D1B2A] outline-none transition-all w-full"
            />
          </div>
          <div>
            <label className="label-caps block mb-1.5">Password</label>
            <div className="relative">
              <input
                data-testid={LOGIN.passwordInput}
                type={showPw ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                className="h-14 border border-[#0D1B2A]/30 bg-transparent rounded-none px-4 pr-12 text-[17px] focus:border-[#0D1B2A] focus:ring-1 focus:ring-[#0D1B2A] outline-none transition-all w-full"
              />
              <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A867D] hover:text-[#0D1B2A]" aria-label="Toggle password">
                {showPw ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
            <div className="mt-1.5 text-right">
              <Link to="/forgot-password" data-testid="login-forgot-password" className="text-sm text-[#B47A3C] hover:text-[#6B4420] font-semibold">
                Forgot password?
              </Link>
            </div>
          </div>
          <button
            data-testid={LOGIN.submitButton}
            type="submit"
            disabled={busy}
            className="w-full h-14 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium text-[17px] inline-flex items-center justify-center gap-2 hover:bg-black transition-transform active:scale-[0.99] disabled:opacity-50"
          >
            {busy ? "Signing in…" : "Sign in"} <ArrowRight size={18} weight="bold" />
          </button>
        </form>

        <p className="text-[15px] text-[#525252] mt-6">
          New to Vintage Vantage?{" "}
          <Link to="/register" data-testid={LOGIN.registerLink} className="font-semibold text-[#0D1B2A] underline underline-offset-4 hover:text-[#B47A3C]">
            Create an account
          </Link>
        </p>
      </div>

      <div className="hidden lg:block relative">
        <img src={AUTH_IMG} alt="Vintage furniture" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0D1B2A]/25" />
        <div className="absolute bottom-10 left-10 right-10 text-[#FDFBF7]">
          <p className="font-serif-display text-3xl leading-tight font-medium max-w-md">
            "Every piece I've researched is saved once — and owned by me."
          </p>
        </div>
      </div>
    </div>
  );
}
